/**
 * Copyright 2026 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useMemo } from 'react';
import { X, FileText, AlertCircle, CheckCircle, Loader, ChevronDown, ChevronRight, Download, Settings } from 'lucide-react';
import type { UserStory, Epic } from '../../types';
import { useApp } from '../../contexts/AppContext';
import { exportToPdf, type PdfExportOptions } from '../../utils/pdfExport';

interface PdfUserStoryExportDialogProps {
  epics: Epic[];
  stories: UserStory[];
  onClose: () => void;
  onExportComplete: () => void;
}

/**
 * Dialog for exporting user stories and epics to PDF.
 * Provides selection of content and formatting options.
 */
export function PdfUserStoryExportDialog({ epics, stories, onClose, onExportComplete }: PdfUserStoryExportDialogProps) {
  const { activeProduct } = useApp();
  
  const [step, setStep] = useState<'select' | 'options' | 'exporting' | 'complete'>('select');
  const [selectedEpics, setSelectedEpics] = useState<Set<string>>(new Set());
  const [selectedStories, setSelectedStories] = useState<Set<string>>(new Set());
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{ filename: string; epicCount: number; storyCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // PDF Options
  const [options, setOptions] = useState<PdfExportOptions>({
    title: 'User Stories',
    subtitle: '',
    includeTableOfContents: true,
    pageSize: 'a4',
    orientation: 'portrait',
    includeStatus: true,
    includeAcceptanceCriteria: true,
    productName: activeProduct?.title || '',
  });

  // Group stories by epic
  const storiesByEpic = useMemo(() => {
    const grouped: Record<string, UserStory[]> = {};
    stories.forEach(story => {
      const epicId = story.epicId || '__no_epic__';
      if (!grouped[epicId]) {
        grouped[epicId] = [];
      }
      grouped[epicId].push(story);
    });
    return grouped;
  }, [stories]);

  const toggleEpicSelection = (epicId: string) => {
    const newSelected = new Set(selectedEpics);
    const epicStories = storiesByEpic[epicId] || [];
    
    if (newSelected.has(epicId)) {
      newSelected.delete(epicId);
      epicStories.forEach(s => selectedStories.delete(s.id));
      setSelectedStories(new Set(selectedStories));
    } else {
      newSelected.add(epicId);
      const newStorySelection = new Set(selectedStories);
      epicStories.forEach(s => newStorySelection.add(s.id));
      setSelectedStories(newStorySelection);
    }
    setSelectedEpics(newSelected);
  };

  const toggleStorySelection = (storyId: string) => {
    const newSelected = new Set(selectedStories);
    if (newSelected.has(storyId)) {
      newSelected.delete(storyId);
    } else {
      newSelected.add(storyId);
    }
    setSelectedStories(newSelected);
  };

  const toggleEpicExpansion = (epicId: string) => {
    const newExpanded = new Set(expandedEpics);
    if (newExpanded.has(epicId)) {
      newExpanded.delete(epicId);
    } else {
      newExpanded.add(epicId);
    }
    setExpandedEpics(newExpanded);
  };

  const selectAll = () => {
    setSelectedEpics(new Set(epics.map(e => e.id)));
    setSelectedStories(new Set(stories.map(s => s.id)));
  };

  const selectNone = () => {
    setSelectedEpics(new Set());
    setSelectedStories(new Set());
  };

  const totalSelected = selectedEpics.size + selectedStories.size;

  const performExport = () => {
    if (totalSelected === 0) {
      setError('Please select at least one epic or story to export.');
      return;
    }

    setExporting(true);
    setError(null);
    setStep('exporting');

    try {
      const result = exportToPdf(
        epics,
        stories,
        selectedEpics,
        selectedStories,
        options
      );

      setExportResult(result);
      setStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
      setStep('options');
    } finally {
      setExporting(false);
    }
  };

  const handleComplete = () => {
    onExportComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Export User Stories to PDF
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Selection Step */}
        {step === 'select' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Select Items to Export</h3>
                <p className="text-sm text-gray-600">
                  {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  Select All
                </button>
                <button
                  onClick={selectNone}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  Select None
                </button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              {epics.length === 0 && stories.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No user stories or epics to export
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {epics.map(epic => {
                    const epicStories = storiesByEpic[epic.id] || [];
                    const isExpanded = expandedEpics.has(epic.id);
                    
                    return (
                      <div key={epic.id}>
                        <div className="flex items-center gap-3 p-3 hover:bg-gray-50">
                          <button
                            onClick={() => toggleEpicExpansion(epic.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <input
                            type="checkbox"
                            checked={selectedEpics.has(epic.id)}
                            onChange={() => toggleEpicSelection(epic.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">{epic.id}</span>
                            <span className="text-gray-600 ml-2">{epic.title}</span>
                          </div>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            Epic • {epicStories.length} stories
                          </span>
                        </div>
                        
                        {isExpanded && epicStories.length > 0 && (
                          <div className="pl-12 bg-gray-50 border-t border-gray-100">
                            {epicStories.map(story => (
                              <div key={story.id} className="flex items-center gap-3 p-2 hover:bg-gray-100">
                                <input
                                  type="checkbox"
                                  checked={selectedStories.has(story.id)}
                                  onChange={() => toggleStorySelection(story.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900">{story.id}</span>
                                  <span className="text-sm text-gray-600 ml-2">{story.title}</span>
                                </div>
                                <span className="text-xs text-gray-500">{story.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Stories without epic */}
                  {storiesByEpic['__no_epic__']?.map(story => (
                    <div key={story.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                      <div className="w-4" /> {/* Spacer */}
                      <input
                        type="checkbox"
                        checked={selectedStories.has(story.id)}
                        onChange={() => toggleStorySelection(story.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{story.id}</span>
                        <span className="text-gray-600 ml-2">{story.title}</span>
                      </div>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        Story (no epic)
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('options')}
                disabled={totalSelected === 0}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue ({totalSelected} selected)
              </button>
            </div>
          </div>
        )}

        {/* Options Step */}
        {step === 'options' && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                PDF Options
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Customize your PDF export.
              </p>
            </div>

            <div className="space-y-6">
              {/* Document Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
                <input
                  type="text"
                  value={options.title}
                  onChange={(e) => setOptions({ ...options, title: e.target.value })}
                  placeholder="User Stories"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Product Name / Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Name / Subtitle</label>
                <input
                  type="text"
                  value={options.productName}
                  onChange={(e) => setOptions({ ...options, productName: e.target.value })}
                  placeholder="My Product"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Page Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Page Size</label>
                  <select
                    value={options.pageSize}
                    onChange={(e) => setOptions({ ...options, pageSize: e.target.value as 'a4' | 'letter' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="a4">A4</option>
                    <option value="letter">Letter</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orientation</label>
                  <select
                    value={options.orientation}
                    onChange={(e) => setOptions({ ...options, orientation: e.target.value as 'portrait' | 'landscape' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>
              </div>

              {/* Content Options */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Content Options</h4>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="includeTableOfContents"
                    checked={options.includeTableOfContents}
                    onChange={(e) => setOptions({ ...options, includeTableOfContents: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="includeTableOfContents" className="text-sm text-gray-700">
                    Include Table of Contents
                  </label>
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="includeStatus"
                    checked={options.includeStatus}
                    onChange={(e) => setOptions({ ...options, includeStatus: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="includeStatus" className="text-sm text-gray-700">
                    Include Status Badges
                  </label>
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="includeAcceptanceCriteria"
                    checked={options.includeAcceptanceCriteria}
                    onChange={(e) => setOptions({ ...options, includeAcceptanceCriteria: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="includeAcceptanceCriteria" className="text-sm text-gray-700">
                    Include Acceptance Criteria
                  </label>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Export Summary</h4>
                <p className="text-sm text-blue-800">
                  {selectedEpics.size} epic{selectedEpics.size !== 1 ? 's' : ''} and {selectedStories.size} stor{selectedStories.size !== 1 ? 'ies' : 'y'} will be exported.
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('select')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={performExport}
                disabled={exporting}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {exporting && <Loader className="w-4 h-4 animate-spin" />}
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            </div>
          </div>
        )}

        {/* Exporting Step */}
        {step === 'exporting' && (
          <div className="p-6 text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Generating PDF...</p>
            <p className="text-sm text-gray-600 mt-2">Creating your document</p>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && exportResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">PDF Export Complete!</h3>
              <p className="text-gray-600">
                Your file has been downloaded: <span className="font-medium">{exportResult.filename}</span>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {exportResult.epicCount}
                </p>
                <p className="text-sm text-purple-900">Epics</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {exportResult.storyCount}
                </p>
                <p className="text-sm text-blue-900">User Stories</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setStep('options');
                  setExportResult(null);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export Again
              </button>
              <button
                onClick={handleComplete}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
